import { t } from '@lingui/macro';

import { ImageWebOnlyProps, SrcsetArray } from '@rover/kibble/core/Image';
import type { Image as ImageType } from '@rover/rsdk/src/apiClient/latest/model/image';

export type ImageSizeFormat = keyof Pick<
  ImageType,
  'smallUncropped' | 'mediumUncropped' | 'largeUncropped' | 'largeUncroppedRetina'
>;
// From aplaceforrover/images/constants.py
export const IMAGE_SIZE_FORMAT_TO_SOURCE_WIDTH: Record<ImageSizeFormat, number> = {
  smallUncropped: 200,
  mediumUncropped: 450,
  largeUncropped: 1024,
  largeUncroppedRetina: 1536,
};

/** The expected widths of an image in the DOM for each breakpoint. */
export type ImageRenderWidthArray = [string, string, string];
/** The image source sizes that will be available for the browser to choose from. */
export type ImageSizeFormatArray = [ImageSizeFormat, ImageSizeFormat, ImageSizeFormat];
/** The relative size that an image will appear given its position
 * and how the layout will be configured for the given image count. */
export type ImageSizeClass = 'small' | 'medium' | 'large';

export type ResponsiveImageProperties = {
  sourceSizeFormats: ImageSizeFormatArray;
  renderWidths: ImageRenderWidthArray;
};

export const IMAGE_SIZE_CLASS_TO_RESPONSIVE_PROPERTIES: Record<
  ImageSizeClass,
  ResponsiveImageProperties
> = {
  small: {
    sourceSizeFormats: ['smallUncropped', 'mediumUncropped', 'largeUncropped'],
    renderWidths: ['100vw', '180px', '180px'],
  },
  medium: {
    sourceSizeFormats: ['smallUncropped', 'mediumUncropped', 'largeUncropped'],
    renderWidths: ['100vw', '370px', '370px'],
  },
  large: {
    sourceSizeFormats: ['mediumUncropped', 'largeUncropped', 'largeUncroppedRetina'],
    renderWidths: ['100vw', '738px', '738px'],
  },
};

/** Gets container sizing for an image given its position (1-indexed)
 * and the total count of all images in the gallery. */
export const getImageContainerSize = (
  position: number,
  totalImageCount: number
): { width: string; height: string } => {
  switch (totalImageCount) {
    case 1:
      return { width: '100%', height: '100%' };
    case 2:
      return { width: '100%', height: '100%' };
    case 3:
      return { width: '100%', height: '50%' };
    case 4:
      return {
        width: position === 4 ? '100%' : '50%',
        height: '50%',
      };
    default:
      return { width: '50%', height: '50%' };
  }
};

/** Gets the size "class" of an image; that is, the image size/quality that should be used given its
 * position (1-indexed) and the total count of all images in the gallery. */
export function getImageSizeClass(position: number, totalImageCount: number): ImageSizeClass {
  // Handle first image separately to keep switch-case neater
  if (position === 1) {
    if (totalImageCount === 1) {
      return 'large';
    }
    return 'medium';
  }

  switch (totalImageCount) {
    case 2:
      return 'medium';
    case 3:
      return 'medium';
    case 4:
      return position === 4 ? 'medium' : 'small';
    default:
      return 'small';
  }
}

export type ImageSourceSetProps = Pick<ImageWebOnlyProps, 'src' | 'srcsetWidths' | 'sizes'>;

/** Gets the relevant source props for an image given its position (1-indexed)
 * and the total count of all images in the gallery. */
export function getSourceSetProps(
  image: ImageType,
  position: number,
  totalImagesCount: number
): ImageSourceSetProps {
  const imageSize = getImageSizeClass(position, totalImagesCount);
  const { sourceSizeFormats, renderWidths } = IMAGE_SIZE_CLASS_TO_RESPONSIVE_PROPERTIES[imageSize];

  const srcsetWidths = sourceSizeFormats.map((sizeFormat) =>
    IMAGE_SIZE_FORMAT_TO_SOURCE_WIDTH[sizeFormat].toString()
  ) as SrcsetArray;
  const src = sourceSizeFormats.map((sizeFormat) => image[sizeFormat]) as SrcsetArray;
  const sizes = renderWidths;

  return { src, srcsetWidths, sizes };
}

export const getAriaLabel = (index: number): string => {
  return t`Image ${index}, button`;
};

export const getAlt = (index: number): string => {
  return t`Image ${index}`;
};
